import { cn } from '@/lib/utils'

/**
 * Booking-channel identification marks -- Airbnb and VRBO's own icon marks on
 * their brand-color chip (Airbnb #FF5A5F, VRBO #163A90), the same nominative
 * use as any "Book on Airbnb" button, not an endorsement claim. Ported from
 * portal-ui's `components/calendar/channel-icons.tsx`, which is the original
 * of this pattern; keep the two in sync if either changes.
 *
 * Airbnb ships as a white glyph on its red chip (matching Airbnb's own
 * app-icon treatment); VRBO ships as its full-color icon mark on its navy
 * chip. Both are square glyphs sized to fill whatever square box the caller
 * gives them (`aspect-square`).
 */
export function AirbnbMark({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'flex aspect-square h-full items-center justify-center rounded-[3px] bg-[#FF5A5F] p-[3px]',
        className
      )}
    >
      <svg
        viewBox="0 0 8 9"
        xmlns="http://www.w3.org/2000/svg"
        className="block h-full w-full"
        role="img"
        aria-label="Airbnb"
      >
        <path
          d="M7.206 6.755a1.24 1.24 0 01-1.394 1.06 1.642 1.642 0 01-.634-.217c-.3-.167-.602-.426-.952-.81.55-.676.885-1.294 1.01-1.845.058-.259.067-.493.042-.71a1.358 1.358 0 00-.226-.568c-.259-.375-.693-.592-1.177-.592-.484 0-.918.225-1.177.592-.117.167-.192.36-.226.568-.033.217-.025.46.042.71.125.551.467 1.177 1.01 1.853-.342.385-.65.643-.952.81a1.64 1.64 0 01-.634.218 1.24 1.24 0 01-1.394-1.06 1.386 1.386 0 01.075-.652c.025-.083.067-.167.109-.267.058-.133.125-.275.191-.417l.009-.017a90.957 90.957 0 011.837-3.75l.025-.05c.067-.124.133-.258.2-.383.067-.134.142-.26.234-.368a.873.873 0 011.336 0c.092.109.167.234.234.368.067.125.133.258.2.383l.026.05A110.51 110.51 0 016.848 5.42v.008c.067.133.125.284.192.418.042.1.084.183.109.267.066.217.091.426.057.643zm-3.331-.393c-.45-.568-.743-1.102-.843-1.553a1.344 1.344 0 01-.025-.51.813.813 0 01.133-.35.887.887 0 01.735-.367c.309 0 .585.134.735.367a.81.81 0 01.133.35c.025.151.017.327-.025.51-.1.443-.392.977-.843 1.553zm3.766-.434c-.042-.1-.084-.208-.125-.3-.067-.15-.134-.293-.193-.426l-.008-.008A87.177 87.177 0 005.47 1.42l-.025-.05c-.067-.126-.133-.26-.2-.393-.084-.15-.167-.309-.3-.459A1.349 1.349 0 003.883 0c-.418 0-.794.184-1.07.5-.125.151-.217.31-.3.46-.067.134-.133.267-.2.392l-.025.05A97.825 97.825 0 00.443 5.176l-.008.017c-.058.134-.125.276-.192.426a5.62 5.62 0 00-.125.3 1.774 1.774 0 001.879 2.404c.275-.033.559-.124.834-.283.343-.192.668-.467 1.035-.868.368.4.702.676 1.036.868a2.204 2.204 0 001.052.3c.234 0 .467-.041.676-.133A1.771 1.771 0 007.716 6.82c.066-.292.033-.584-.075-.893z"
          fill="#fff"
        />
      </svg>
    </span>
  )
}

export function VrboMark({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'flex aspect-square h-full items-center justify-center rounded-[3px]',
        className
      )}
    >
      <svg
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
        className="block h-full w-full"
        role="img"
        aria-label="VRBO"
      >
        <path
          d="M0 6a6 6 0 016-6h12a6 6 0 016 6v12a6 6 0 01-6 6H6a6 6 0 01-6-6V6z"
          fill="#163A90"
        />
        <path
          d="M14.76 10.79l.04.02c.96-3.17 1.27-4.57.42-5.42h-1.2v.03c1.34.47 1.64 1.83.74 5.37z"
          fill="#8EC089"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M18.57 5.39c-1.28.74-2.04 2.12-3.65 5.5l.04.03c1.85-3.43 2.9-4.8 5.28-5.5v-.03h-1.67z"
          fill="#2474DE"
        />
        <path
          d="M14.84 10.84l.04.03c1.51-3.56 1.91-4.53 2.53-5.45v-.03h-1.23v.04c.13 1.41-.36 2.72-1.34 5.41z"
          fill="#E85F24"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M4.75 5.39H3.66v.03c1 .38 1.48 1.13 1.87 2.88L8.2 20.55h.61L6.12 8.2c-.46-2.1-.95-2.5-1.37-2.8z"
          fill="#76C5F7"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M10.44 19.7l.4-.84L8.58 8.29c-.4-1.64-.3-2.39-.14-2.9h-.87a9.4 9.4 0 00.34 2.63l2.53 11.67z"
          fill="#2474DE"
        />
        <path
          d="M6.78 5.39H5.76v.03c.46.54.59 1.2.95 2.67l2.71 12.46h.61L7.31 8.05c-.38-1.6-.45-2.04-.53-2.66z"
          fill="#318EEE"
        />
        <path
          d="M11.25 18.01l.4-.84-1.8-8.69c-.35-1.69-.2-2.56.36-3.06v-.03h-.96c-.3.63-.4 1.26-.05 2.93l2.05 9.7z"
          fill="#E85F24"
        />
        <path
          d="M10.49 8.54c-.5-2.33.38-2.95.8-3.15h1.43v.03c-1.55.3-2 1.15-1.63 3.1l1.38 6.96-.4.85-1.58-7.8z"
          fill="#8EC089"
        />
      </svg>
    </span>
  )
}

/** Maps a reservation/message channel string to the right mark. No square icon-only
 * mark exists for direct bookings or channels admin-ui doesn't recognize, so callers
 * that need to cover those cases should check `channel` themselves before rendering
 * this -- unlike portal-ui's `ChannelMark`, this has no "direct" fallback mark. */
export function ChannelMark({
  channel,
  className,
}: {
  channel: string | null | undefined
  className?: string
}) {
  const normalized = (channel || '').trim().toLowerCase()
  if (normalized === 'airbnb' || normalized === 'airbnb_official') {
    return <AirbnbMark className={className} />
  }
  if (normalized === 'vrbo' || normalized === 'home_away' || normalized === 'homeaway') {
    return <VrboMark className={className} />
  }
  return null
}
